import type { TextRunStyleUpdate } from '@/lib/text/types/text-run-style-update'
import type { ShapeTextCharRunMap, ShapeTextFonts } from '../types/shape-text-layout-state'

/**
 * The style update for Ctrl/Cmd+B (bold) or Ctrl/Cmd+I (italic): toggle the bold/italic variant
 * within the same font family. Mirrors the contentEditable text-box engine's strategy
 * (text-renderer.tsx) — a real selection remaps each distinct fontId in range to its bold/italic
 * sibling via `fontIdByFontId` (runs whose family has no such variant are silently left as-is); a
 * collapsed caret resolves the variant from the caret's own run and applies it directly (deferred
 * to next-typed-characters by `applyRunStyle` itself, same as any other run field).
 *
 * Null when there is nothing to change. `getEditingStyle` is only consulted for a collapsed caret.
 */
export function resolveBoldItalicToggle(
  kind: 'bold' | 'italic',
  {
    selStart,
    selEnd,
    charRunMap,
    fonts,
    getEditingStyle,
  }: {
    selStart: number
    selEnd: number
    charRunMap: ShapeTextCharRunMap
    fonts: NonNullable<ShapeTextFonts>
    getEditingStyle: () => TextRunStyleUpdate | null
  }
): TextRunStyleUpdate | null {
  if (selEnd > selStart) {
    const fontIdsInRange = new Set<number>()

    for (let i = selStart; i < selEnd; i++) {
      const fontId = charRunMap[i]?.fontId

      if (typeof fontId === 'number') fontIdsInRange.add(fontId)
    }

    const allActive = [...fontIdsInRange].every(
      (id) => (fonts.getVariantByFontId(id)?.[kind] ?? false) === true
    )
    const target = !allActive
    const fontIdByFontId: Record<number, number> = {}

    for (const fontId of fontIdsInRange) {
      const variant = fonts.getVariantByFontId(fontId)

      if (!variant || variant.fontFamilyId == null) continue
      const famId = variant.fontFamilyId
      const targetFontId =
        kind === 'bold'
          ? fonts.getFontIdByVariant(famId, target, variant.italic ?? false)
          : fonts.getFontIdByVariant(famId, variant.bold ?? false, target)

      if (targetFontId != null) fontIdByFontId[fontId] = targetFontId
    }

    return Object.keys(fontIdByFontId).length > 0 ? { fontIdByFontId } : null
  }

  const style = getEditingStyle()
  const famId = style?.fontFamilyId
  const fontId = style?.fontId
  const currentVariant = fontId != null ? fonts.getVariantByFontId(fontId) : undefined
  const target = !(currentVariant?.[kind] ?? false)

  if (famId == null) return null

  const targetFontId =
    kind === 'bold'
      ? fonts.getFontIdByVariant(famId, target, currentVariant?.italic ?? false)
      : fonts.getFontIdByVariant(famId, currentVariant?.bold ?? false, target)

  return targetFontId != null ? { fontId: targetFontId } : null
}
